import axios from "./axios";

export async function fetchHours(controller) {
  return axios({
    method: "get",
    url: "/hours",
    params: controller,
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchHour(id, options = {}) {
  return axios.get(`/hours/` + id, options);
}

export async function fetchHoursCount(payload) {
  return axios.get(`/hours/count`, {
    params: payload,
  });
}

export async function fetchHourSettings() {
  return axios.get(`/hours/settings`);
}

export async function fetchHourRegistrationPolicy() {
  return axios.get(`/hours/registration_policy`);
}

export async function updateHourSettings(payload) {
  return axios
    .put(`/hours/settings/`, { settings: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function queueFetchHoursJob(params) {
  // Expects: { from_date: 'YYYY-MM-DD', to_date: 'YYYY-MM-DD', user_id: number | null }
  return axios
    .post(`/tripletex/fetch_hours`, params)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function createHour(payload) {
  return axios
    .post(`/hours`, { hour: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}
export async function updateHour(payload) {
  return axios
    .put(`/hours/${payload.id}`, { hour: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

/** PATCH /hours/bulk_reassign — move many hours to a project activity line */
export async function bulkReassignHours({ hour_ids, project_activity_id }) {
  const res = await axios.patch(
    `/hours/bulk_reassign`,
    {
      hour_ids,
      project_activity_id,
    },
    { validateStatus: (status) => [200, 422].includes(status) }
  );
  if (res.status && res.status >= 400 && res.status !== 422) {
    const err = new Error("bulk_reassign_failed");
    err.response = res;
    throw err;
  }
  return res.data;
}

/**
 * Batch create/update/destroy for desktop «Rask» time entry.
 * POST /hours/batch_upsert — graceful when endpoint is temporarily missing (404/501).
 *
 * @param {{ entries: Array<object>, user_id?: number|string }} payload
 * @returns {Promise<{ unavailable: boolean, saved: Array, errors: Array, status?: number, raw?: any }>}
 */
export async function batchUpsertHours(payload = {}) {
  const { entries = [], user_id } = payload;
  const body = { entries };
  if (user_id != null && user_id !== "") body.user_id = user_id;

  try {
    const res = await axios.post(`/hours/batch_upsert`, body, {
      validateStatus: (status) =>
        status === 200 ||
        status === 422 ||
        status === 403 ||
        status === 404 ||
        status === 501,
    });

    if (res.status === 404 || res.status === 501) {
      return { unavailable: true, saved: [], errors: [], status: res.status };
    }

    const payloadData = res.data?.data ?? res.data ?? {};
    const topErrors = Array.isArray(res.data?.errors) ? res.data.errors : [];
    const entryErrors = Array.isArray(payloadData.errors) ? payloadData.errors : [];
    // 403 (e.g. user_id forbidden) returns top-level errors[] without per-entry data.
    const errors =
      entryErrors.length > 0
        ? entryErrors
        : topErrors.length > 0
          ? topErrors.map((msg, index) => ({
              index,
              errors: [typeof msg === "string" ? msg : String(msg)],
            }))
          : [];

    return {
      unavailable: false,
      saved: Array.isArray(payloadData.saved) ? payloadData.saved : [],
      errors,
      status: res.status,
      raw: res.data,
    };
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404 || status === 501) {
      return { unavailable: true, saved: [], errors: [], status };
    }
    throw error;
  }
}

/**
 * Split one hour across multiple project activity lines.
 * POST /hours/:id/split — { lines: [{ project_activity_id, time, description? }, ...] }
 * Sum of line times must equal original.time (tolerance 0.001).
 */
export async function splitHour(id, payload = {}) {
  const res = await axios.post(`/hours/${id}/split`, payload, {
    validateStatus: (status) => [200, 201, 422].includes(status),
  });
  if (res.status === 422) {
    const err = new Error("split_hour_validation");
    err.response = res;
    throw err;
  }
  if (res.status && res.status >= 400) {
    const err = new Error("split_hour_failed");
    err.response = res;
    throw err;
  }
  return res.data;
}

export async function deleteHour(id) {
  return axios
    .delete(`/hours/${id}`)

    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function fetchHoursMonthlyOverview(params = {}) {
  return axios.get(`/hours/monthly_overview`, { params });
}

export async function updateHoursMonthlyOverviewUnlock(payload) {
  return axios.put(`/hours/monthly_overview/unlock`, payload);
}

export async function fetchHoursResourcePlan(params = {}) {
  return axios.get(`/hours/resource_plan`, { params });
}

function hourAgreedOvertimeActionError(data, status) {
  const err = new Error("hour_agreed_overtime_action_failed");
  err.response = { status: status || 422, data: data || { errors: ["hour_agreed_overtime_action_failed"] } };
  return err;
}

function agreedOvertimeHourRecord(body) {
  if (!body || typeof body !== "object") return null;
  if (Object.prototype.hasOwnProperty.call(body, "agreed_overtime_confirmed")) return body;
  if (body.data && typeof body.data === "object") return body.data;
  return null;
}

/**
 * Confirm agreed overtime (admin / hours_handler).
 * POST /hours/:id/confirm_agreed_overtime only — PUT cannot set confirmation (not mass-assignable).
 */
export async function confirmAgreedOvertime(id, payload = {}) {
  const note = payload?.note ?? payload?.agreed_overtime_note;
  const res = await axios.post(
    `/hours/${id}/confirm_agreed_overtime`,
    note != null && note !== "" ? { note } : {},
    { validateStatus: (status) => status < 500 }
  );

  if (res.status >= 200 && res.status < 300) {
    const record = agreedOvertimeHourRecord(res.data);
    if (record?.agreed_overtime_confirmed !== true) {
      throw hourAgreedOvertimeActionError(res.data, 422);
    }
    return res.data;
  }

  throw hourAgreedOvertimeActionError(res.data, res.status);
}

function agreedOvertimeRejected(record) {
  if (!record) return false;
  return record.agreed_overtime_requested !== true && record.agreed_overtime_confirmed !== true;
}

/**
 * Reject / clear agreed overtime request (admin / hours_handler).
 * POST /hours/:id/reject_agreed_overtime only — PUT is not the confirm/reject contract
 * and can 200 while overtime flags stay pending.
 */
export async function rejectAgreedOvertime(id, payload = {}) {
  const note = payload?.note ?? payload?.agreed_overtime_note;
  const res = await axios.post(
    `/hours/${id}/reject_agreed_overtime`,
    note != null && note !== "" ? { note } : {},
    { validateStatus: (status) => status < 500 }
  );

  if (res.status >= 200 && res.status < 300) {
    const record = agreedOvertimeHourRecord(res.data);
    if (!agreedOvertimeRejected(record)) {
      throw hourAgreedOvertimeActionError(res.data, 422);
    }
    return res.data;
  }

  throw hourAgreedOvertimeActionError(res.data, res.status);
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoking synchronously can cancel the download in some browsers.
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
}

async function parseHoursExportErrorBlob(blob) {
  if (!(blob instanceof Blob)) return null;
  const text = await blob.text();
  try {
    return JSON.parse(text);
  } catch (_e) {
    return null;
  }
}

async function downloadHoursExport(path, params, filename, mime) {
  let response;
  try {
    // hour_ids pin a client-filtered selection (company overview, up to 5000).
    // POST them in the JSON body — a GET query of that size hits URL limits.
    const usePost = Array.isArray(params?.hour_ids);
    response = usePost
      ? await axios.post(path, params, { responseType: "blob" })
      : await axios.get(path, {
          params,
          responseType: "blob",
        });
  } catch (error) {
    // Axios rejects 4xx with responseType blob — rehydrate problem/errors so
    // Operation Feedback can show reload on HOURS_EXPORT_SELECTION_CHANGED (409).
    const json = await parseHoursExportErrorBlob(error?.response?.data);
    if (json) {
      const message =
        json.problem?.message ||
        (Array.isArray(json.errors) ? json.errors.join(", ") : null) ||
        json.error ||
        "export_failed";
      const next = new Error(message);
      next.code = json.problem?.code;
      next.response = { ...error.response, data: json, status: error.response?.status };
      throw next;
    }
    throw error;
  }
  const blob = response.data;
  if (blob instanceof Blob) {
    const head = String.fromCharCode(
      ...new Uint8Array(await blob.slice(0, 5).arrayBuffer())
    );
    if (head.startsWith("{") || head.startsWith("<")) {
      const json = await parseHoursExportErrorBlob(blob);
      const message =
        json?.problem?.message ||
        (Array.isArray(json?.errors) ? json.errors.join(", ") : null) ||
        json?.error ||
        "export_failed";
      const next = new Error(message);
      next.code = json?.problem?.code;
      next.response = { status: response.status, data: json || { error: message } };
      throw next;
    }
  }
  triggerBlobDownload(new Blob([blob], { type: mime }), filename);
  return true;
}

export async function exportHoursPdf(params) {
  const from = params?.date_from || "periode";
  const to = params?.date_to || "";
  return downloadHoursExport(
    "/hours/export_pdf",
    params,
    `timeliste_${from}_${to}.pdf`,
    "application/pdf"
  );
}

export async function exportHoursExcel(params) {
  const from = params?.date_from || "periode";
  const to = params?.date_to || "";
  return downloadHoursExport(
    "/hours/export_excel",
    params,
    `timeliste_${from}_${to}.xlsx`,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
}
